// src/hooks/useBuyerTemplateData.js
// This hook loads and stores templateData only (static, unchanging).

import { useEffect, useRef, useState } from "react";
import { getUserTemplate } from "../services/buyerNetService";

export const useBuyerTemplateData = (mainId, userId, companyId) => {
  const [templateData, setTemplateData] = useState(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!mainId || !userId || !companyId) return;

    const contextKey = `${mainId}-${userId}-${companyId}`;
    if (hasLoadedRef.current === contextKey) return;
    hasLoadedRef.current = contextKey;

    const safeParse = (data) => {
      if (!data) return null;
      if (Array.isArray(data) || typeof data === "object") return data;
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    };

    const toCamel = (s = "") =>
      s
        .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
        .replace(/^([A-Z])/, (m) => m.toLowerCase());

    const parseValue = (item) => {
      if (!item) return "0.00";
      const v = item.value ?? item.rate ?? item.Cash ?? item.total ?? null;
      if (v == null) return "0.00";
      if (typeof v === "string") {
        const s = v.replace(/,/g, "").trim();
        return s === "" ? "0.00" : s;
      }
      return String(v);
    };

    const parseTotal = (item) => {
      if (!item) return "0.00";
      const v =
        item.total != null && item.total !== ""
          ? item.total
          : (item.value ?? item.rate ?? item.Cash ?? null);
      if (v == null) return "0.00";
      if (typeof v === "string") {
        const s = v.replace(/,/g, "").trim();
        return s === "" ? "0.00" : s;
      }
      return String(v);
    };

    const parseArraySection = (arr) => {
      const parsed = [];
      const data = safeParse(arr);
      if (!Array.isArray(data)) return parsed;
      for (const it of data) {
        if (!it?.key) continue;
        parsed.push({
          ...it,
          camelKey: toCamel(it.key),
          value: parseValue(it),
          total: parseTotal(it),
        });
      }
      return parsed;
    };

    const fetchTemplate = async () => {
      try {
        const response = await getUserTemplate(companyId);
        const data = response;
        if (!data) return;

        const cash_section = [
          {
            key: "origination_factor",
            camelKey: "originationFactor",
            label: "Origination Factor",
            unit: "%",
            value: "0.00",
          },
          {
            key: "document_preparation",
            camelKey: "documentPreparation",
            label: "Document Preparation",
            unit: "$",
            value: "0.00",
          },
          {
            key: "tax_service_contract",
            camelKey: "taxServiceContract",
            label: "Tax Service Contract",
            unit: "$",
            value: "0.00",
          },
          {
            key: "underwriting",
            camelKey: "underwriting",
            label: "Underwriting",
            unit: "$",
            value: "0.00",
          },
          {
            key: "processing_fee",
            camelKey: "processingFee",
            label: "Processing Fee",
            unit: "$",
            value: "0.00",
          },
          {
            key: "appraisal_fee",
            camelKey: "appraisalFee",
            label: "Appraisal Fee",
            unit: "$",
            value: "0.00",
          },
        ];

        const buyerSections = {
          tenure: parseArraySection(data.buyer_tenure_section),
          insurance: parseArraySection(data.buyer_taxes_insurance_section),
          prepaid: parseArraySection(data.buyer_prepaid_section),
          payments: parseArraySection(data.buyer_payments_section),
          closingCosts: parseArraySection(data.buyer_closing_costs_section),
          fha: parseArraySection(data.fha_closing_costs),
          va: parseArraySection(data.va_closing_costs),
          usda: parseArraySection(data.usda_closing_costs),
          convAdj: parseArraySection(data.conventional_adjustable_section),
          conventional: parseArraySection(
            data.conventional_closing_costs_section,
          ),
          cash: parseArraySection(cash_section),
          commission_buyer: data.commission_buyer
        };

        const creditReport = buyerSections.payments.find(
          (it) => it.key === "credit_report",
        );

        // TEMP DEBUG — buyerSections pipeline trace, step 1: raw credit_report
        // as configured in admin's buyer_payments_section, before it gets
        // copied into any loan-type-specific array below.
        // taxDebugLog("useBuyerTemplateData: credit_report found in payments", {
        //   creditReport,
        // });

        if (creditReport) {
          ["conventional", "fha", "va", "usda", "cash"].forEach((type) => {
            buyerSections[type] = [...buyerSections[type], creditReport];
          });
        }

        buyerSections.conventional = buyerSections.conventional.filter(
          (it) => it.key !== "points_pct",
        );

        // Apply to Cash: credit_report (or any other row explicitly marked
        // apply:"N") doesn't apply to a Cash transaction — zero it out only
        // in the Cash variant. apply:"Y" (or no apply key at all, e.g. the
        // hardcoded cash_section fee rows) keeps its configured value.
        buyerSections.cash = buyerSections.cash.map((row) =>
          row?.apply === "N" ? { ...row, value: "0.00", total: "0.00" } : row,
        );

        // TEMP DEBUG — buyerSections pipeline trace, step 2: credit_report's
        // final resolved value inside the Cash variant, after the Apply to
        // Cash zero-out above.
        // taxDebugLog("useBuyerTemplateData: credit_report resolved for cash", {
        //   creditReportInCash: buyerSections.cash.find(
        //     (it) => it.key === "credit_report",
        //   ),
        // });

     //   console.log("Loaded buyer template data:", buyerSections);

        setTemplateData({ buyerSections });
      } catch (err) {
        console.error("Error loading buyer template:", err);
      }
    };

    fetchTemplate();
  }, [mainId, userId, companyId]);

  return templateData;
};
